import React, { useEffect, useState, useCallback } from 'react';
import { IconButton, Badge, Menu, MenuItem, Box, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import Link from 'components/common/Link';
import NotificationList, {
  NotificationItem,
  NotificationsResponse,
  NOTIFICATIONS_UNREAD_EVENT,
} from 'components/notifications/NotificationList';

const DROPDOWN_LIMIT = 10;

const Notifications = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await sendApiRequest({
        path: `/notifications?limit=${DROPDOWN_LIMIT}`,
        method: 'GET',
      })) as NotificationsResponse;

      if (response) {
        setNotifications(response.list || response.notifications || []);
        setUnreadCount(response.unreadCount ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      notify(error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const onUnreadChanged = (event: Event) => {
      const unread = (event as CustomEvent<{ unreadCount: number }>).detail?.unreadCount;
      if (typeof unread === 'number') {
        setUnreadCount(Math.max(0, unread));
      }
    };

    window.addEventListener(NOTIFICATIONS_UNREAD_EVENT, onUnreadChanged);
    return () => window.removeEventListener(NOTIFICATIONS_UNREAD_EVENT, onUnreadChanged);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    void fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showAllLink = !loading && notifications.length > 0;

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'text.primary',
        }}
        aria-label="notifications"
      >
        <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              maxHeight: 440,
              mt: 1,
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'divider',
            },
          },
          list: {
            sx: { py: 0 },
          },
        }}
      >
        <Box
          component="li"
          sx={{
            listStyle: 'none',
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
            Notifications
          </Typography>
        </Box>

        <Box component="li" sx={{ listStyle: 'none', bgcolor: 'background.paper' }}>
          <NotificationList
            notifications={notifications}
            loading={loading}
            maxHeight={340}
            onCloseExtra={handleClose}
            onChange={(nextNotifications, unreadDelta) => {
              setNotifications(nextNotifications);
              setUnreadCount(prev => Math.max(0, prev + unreadDelta));
            }}
          />
        </Box>

        {showAllLink && (
          <MenuItem
            component={Link}
            href="/notifications"
            onClick={handleClose}
            sx={{
              justifyContent: 'center',
              py: 1,
              minHeight: 32,
              borderTop: '1px solid',
              borderColor: 'divider',
              typography: 'body2',
              fontWeight: 500,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Show all
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default Notifications;
